<template>
  <BaseTemplate>
    <div class="user-register">
      <Title eng="SIGN UP" :jpn="t('会員登録')" />
      <div class="text over" v-if="i18n_mode != 'jp'">日本人の方は日本語で入力をお願いいたします</div>
      <div class="text description">{{ t('以下に登録情報をご入力ください。') }}</div>
      <div class="input-area">
        <SubTitle>{{ t('氏名') }}</SubTitle>
        <div class="user-name">
          <div class="last-name-container">
            <TextBox class="text-box last-name" inputType="text" :value="lastName" :placeholder="t('姓')"
              @handleTextInput="inputLastName" />
            <ErrorMessage v-if="errorMessageLastName" class="error-message" :message="errorMessageLastName" />
          </div>
          <div class="first-name-container">
            <TextBox class="text-box first-name" inputType="text" :value="firstName" :placeholder="t('名')"
              @handleTextInput="inputFirstName" />
            <ErrorMessage v-if="errorMessageFirstName" class="error-message" :message="errorMessageFirstName" />
          </div>
        </div>
        <SubTitle>{{ t('住所') }}</SubTitle>
        <div class="address_area">
          <LabelInput class="label-input" :labelText="t('郵便番号')" :annotation="t('「−」ハイフンは不要です')">
            <div class="announce-area">
              <TextBox class="text-box zip-code" inputType="Number" :value="zipCode" placeholder="0000000"
                @handleTextInput="inputZipCode" @handleTextChange="changeZipCode" />
              <div class="announce">※ハイフンは不要です</div>
            </div>
            <ErrorMessage v-if="errorMessageZipCode" class="error-message" :message="errorMessageZipCode" />
          </LabelInput>
          <LabelInput class="label-input" labelText="都道府県" v-if="i18n_mode == 'jp'">
            <SelectBox class="select-box prefecture-id" :options="prefectureList" :selectedOption="prefectureId"
              placeholder="" @handleSelectChange="selectPrefectureId" />
            <ErrorMessage v-if="errorMessagePrefectureId" class="error-message" :message="errorMessagePrefectureId" />
          </LabelInput>
          <LabelInput class="label-input" :labelText="t('国名')" v-if="i18n_mode != 'jp'">
            <TextBox class="text-box shiku" inputType="text" :value="country" placeholder=""
              @handleTextInput="inputCountry" />
            <ErrorMessage v-if="errorMessageCountry" class="error-message" :message="errorMessageCountry" />
          </LabelInput>
          <LabelInput class="label-input" :labelText="t('市区町村')">
            <TextBox class="text-box shiku" inputType="text" :value="shiku" placeholder=""
              @handleTextInput="inputShiku" />
            <ErrorMessage v-if="errorMessageShiku" class="error-message" :message="errorMessageShiku" />
          </LabelInput>
          <LabelInput class="label-input" :labelText="t('番地')">
            <TextBox class="text-box address" inputType="text" :value="address" :placeholder="t('番地を入力してください')"
              @handleTextInput="inputAddress" />
            <ErrorMessage v-if="errorMessageAddress" class="error-message" :message="errorMessageAddress" />
          </LabelInput>
          <LabelInput class="label-input" :labelText="t('建物名・部屋番号')" :annotation="t('配送トラブル防止のため、該当する場合は略さずに必ず記載して下さい。')">
            <TextBox class="text-box address" inputType="text" :value="building" :placeholder="t('建物名・部屋番号を入力してください')"
              @handleTextInput="inputBuilding" />
            <ErrorMessage v-if="errorMessageBuilding" class="error-message" :message="errorMessageBuilding" />
          </LabelInput>
        </div>

        <SubTitle subtext="※ハイフンは不要です">{{ t('電話番号') }}</SubTitle>
        <div class="address_area">
          <TextBox class="text-box tel" inputType="Number" :value="tel" placeholder="01234567890"
            @handleTextInput="inputTel" />
          <ErrorMessage v-if="errorMessageTel" class="error-message" :message="errorMessageTel" />
        </div>

        <SubTitle subtext="※@yahooや@gmailなどを推奨します">{{ t('メールアドレス') }}</SubTitle>
        <div class="address_area">
          <TextBox class="text-box mail" inputType="mail" :value="mail" placeholder="example@gmail.com"
            @handleTextInput="inputMail" />
          <ErrorMessage v-if="errorMessageMail" class="error-message" :message="errorMessageMail" />
        </div>

        <SubTitle>{{ t('パスワード') }}</SubTitle>
        <div class="address_area">
          <TextBox class="text-box password row" inputType="password" :value="password"
            :placeholder="t('半角英数字6文字以上20文字以内')" @handleTextInput="inputPassword" />
          <ErrorMessage v-if="errorMessagePassword" class="error-message" :message="errorMessagePassword" />
          <TextBox class="text-box password row confirm" inputType="password" :value="confirmPass"
            :placeholder="t('半角英数字6文字以上20文字以内')" @handleTextInput="inputConfirmPass" />
          <ErrorMessage v-if="errorMessagePassword" class="error-message" :message="errorMessagePassword" />
        </div>

        <SubTitle>{{t('紹介コード')}}</SubTitle>
        <div class="address_area">
          <TextBox
            class="text-box mail"
            inputType="text"
            :value="referralCode"
            :placeholder="t('紹介コードを入力してください')"
            @handleTextInput="inputReferralCode"
          />
          <ErrorMessage
            v-if="errorMessageReferralCode"
            class="error-message"
            :message="errorMessageReferralCode"
          />
        </div>

        <div class="address_area">
          <div class="agree_area">
            <label @click="changeAgreeCheckbox">
              <CheckBox :isChecked="agreeCheck" />
              <div class="agree_label">利用規約に同意して会員登録を行う</div>
            </label>
          </div>
        </div>

        <BlackGradationButton v-if="agreeCheck" class="square-button" :text="t('入力内容を確認')"
          @handleSquareBtnClick="goToConfirmUserRegister" />
      </div>
    </div>
  </BaseTemplate>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseTemplate from '@/components/templates/BaseTemplate.vue';
import TextBox from '@/components/atoms/TextBox.vue';
import CheckBox from '@/components/atoms/CheckBox.vue';
import SelectBox from '@/components/atoms/SelectBox.vue';
import ErrorMessage from '@/components/atoms/ErrorMessage.vue';
import LabelInput from '@/components/molecules/LabelInput.vue';
import Title from '@/components/molecules/Title.vue';
import SubTitle from '@/components/molecules/SubTitle.vue';
import BlackGradationButton from '@/components/atoms/BlackGradationButton.vue';
import t from '@/plugins/i18n';

export default {
  name: 'UserRegister',
  mixins: [t],

  components: {
    BaseTemplate,
    TextBox,
    CheckBox,
    SelectBox,
    ErrorMessage,
    LabelInput,
    Title,
    SubTitle,
    BlackGradationButton,
  },

  data: () => ({
    lastName: null,
    middleName: null,
    firstName: null,
    tel: null,
    mail: null,
    zipCode: null,
    prefectureId: null,
    country: null,
    shiku: null,
    agreeCheck: false,
    address: null,
    building: null,
    password: null,
    confirmPass: null,
    referralCode: null,
    errorMessageLastName: null,
    errorMessageFirstName: null,
    errorMessageTel: null,
    errorMessageMail: null,
    errorMessageZipCode: null,
    errorMessagePrefectureId: null,
    errorMessageShiku: null,
    errorMessageAddress: null,
    errorMessageBuilding: null,
    errorMessagePassword: null,
    errorMessageReferralCode: null,
  }),

  computed: {
    ...mapGetters({
      prefectureList: 'prefecture/prefectureList',
      currentAddress: 'zipCode/currentAddress',
      confirmUserResult: 'user/confirmUserResult',
    }),
  },

  watch: {
    currentAddress(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          const { result } = newVal;
          this.selectPrefectureId(result[0].prefcode);
          this.inputShiku(`${result[0].address2}${result[0].address3}`);
        }
      }
    },
    confirmUserResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          const query = {
            lastName: this.lastName,
            middleName: this.middleName,
            firstName: this.firstName,
            tel: this.tel,
            mail: this.mail,
            zipCode: this.zipCode,
            country: this.country,
            prefectureId: this.prefectureId,
            shiku: this.shiku,
            address: this.address,
            building: this.building,
            password: this.password,
            confirmPass: this.confirmPass,
            referralCode: this.referralCode,
          };
          this.$store.dispatch('user/clearConfirmUserResult');
          this.$router.push({ path: '/confirm-user-register', query });
        } else {
          const {
            lastName, middleName, firstName, tel, mail, zipcode, country,
            pref_id: prefId, shiku, address, building, pass,
            referral_code: referralCode,
          } = newVal.errors;
          this.errorMessageLastName = lastName ? lastName[0] : null;
          this.errorMessageFirstName = firstName ? firstName[0] : null;
          this.errorMessageMiddleName = middleName ? middleName[0] : null;
          this.errorMessageTel = tel ? tel[0] : null;
          this.errorMessageMail = mail ? mail[0] : null;
          this.errorMessageZipCode = zipcode ? zipcode[0] : null;
          this.errorMessageCountry = country ? country[0] : null;
          this.errorMessagePrefectureId = prefId ? prefId[0] : null;
          this.errorMessageShiku = shiku ? shiku[0] : null;
          this.errorMessageAddress = address ? address[0] : null;
          this.errorMessageBuilding = building ? building[0] : null;
          this.errorMessagePassword = pass ? pass[0] : null;
          this.errorMessageReferralCode = referralCode ? referralCode[0] : null;
        }
      }
    },
  },

  created() {
    this.lastName = this.$route.query.lastName || null;
    this.middleName = this.$route.query.middleName || null;
    this.firstName = this.$route.query.firstName || null;
    this.tel = this.$route.query.tel || null;
    this.mail = this.$route.query.mail || null;
    this.zipCode = this.$route.query.zipCode || null;
    this.prefectureId = this.$route.query.prefectureId || null;
    this.country = this.$route.query.country || null;
    this.shiku = this.$route.query.shiku || null;
    this.address = this.$route.query.address || null;
    this.building = this.$route.query.building || null;
    this.password = this.$route.query.password || null;
    this.confirmPass = this.$route.query.confirmPass || null;
    this.referralCode = this.$route.query.referralCode || null;
    this.$store.dispatch('prefecture/setPrefectureList');
  },

  methods: {
    inputLastName(lastName) {
      this.lastName = lastName;
    },
    inputFirstName(firstName) {
      this.firstName = firstName;
    },
    inputMiddleName(name) {
      this.middleName = name;
    },
    inputTel(tel) {
      this.tel = tel;
    },
    inputMail(mail) {
      this.mail = mail;
    },
    inputZipCode(zipCode) {
      this.zipCode = zipCode;
    },
    inputCountry(country) {
      this.country = country;
    },
    selectPrefectureId(prefectureId) {
      this.prefectureId = prefectureId;
    },
    inputShiku(shiku) {
      this.shiku = shiku;
    },
    inputAddress(address) {
      this.address = address;
    },
    inputBuilding(building) {
      this.building = building;
    },
    inputPassword(password) {
      this.password = password;
    },
    inputConfirmPass(confirmPass) {
      this.confirmPass = confirmPass;
    },
    inputReferralCode(referralCode) {
      this.referralCode = referralCode;
    },
    changeZipCode() {
      if (this.i18n_mode === 'jp') {
        const zipCodeLength = 7;
        if (String(this.zipCode).length === zipCodeLength) {
          const postParams = {
            zipcode: this.zipCode,
          };
          this.$store.dispatch('zipCode/setCurrentAddress', { postParams });
        }
      }
    },
    changeAgreeCheckbox() {
      this.agreeCheck = !this.agreeCheck;
    },
    goToConfirmUserRegister() {
      if (this.i18n_mode !== 'jp') {
        this.prefectureId = 99;
      }
      const postParams = {
        lastName: this.lastName,
        middleName: this.middleName,
        firstName: this.firstName,
        tel: this.tel,
        mail: this.mail,
        zipcode: this.zipCode,
        country: this.country,
        pref_id: this.prefectureId,
        shiku: this.shiku,
        address: this.address,
        building: this.building,
        pass: this.password,
        confirmPass: this.confirmPass,
        // referralCode: this.referralCode,
        referralCode: '',
      };
      this.$store.dispatch('user/confirmUser', { postParams });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-register {
  width: 100%;
  max-width: 600px;
  min-height: 100%;
  padding: 50px 30px;
  margin: 0px auto;

  .description {
    display: inline-block;
    color: $black;
    height: 20px;
    margin-top: 30px;
    font-family: $notoSans;
    font-size: 14px;
    line-height: 23.4px;
    text-align: center;
  }

  .input-area {
    margin-top: 20px;

    .user-name {
      display: flex;
      width: 100%;
      margin-top: 10px;
      margin-bottom: 10px;
      padding: 0 30px;

      .last-name-container {
        flex: 1 1 150px;
        min-width: 0;
      }

      .first-name-container {
        flex: 1 1 150px;
        min-width: 0;
        margin-left: 15px;
      }

      .text-box {
        margin-top: 0px;
      }
    }
  }

  .over {
    color: $yellow !important;
  }

  .announce-area {
    display: flex;
    align-items: center;

    .announce {
      color: $black;
      margin-left: 10px;
      font-size: 14px;
      width: 300px;
    }
  }

  .address_area {
    padding: 0 30px;
    margin: 10px 0;

    .row {
      margin: 5px 0;
    }
  }

  .label-input {
    width: 100%;
    margin-top: 10px;
    max-width: 680px;
    margin-left: auto;
    margin-right: auto;

    .text-box {
      width: 100%;
      height: 40px;
    }

    .select-box {
      margin-top: 10px;
      width: 100%;
      height: 40px;
    }
  }

  .square-button {
    width: 200px;
    height: 50px;
    margin: 40px auto 0px;
  }
}

.flex_box {
  display: flex;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;

  .label-input {
    &:nth-child(1) {
      margin-right: 5px;
    }

    &:nth-child(2) {
      margin-left: 5px;
      margin-right: 5px;
    }

    &:nth-child(3) {
      margin-left: 5px;
    }
  }
}

.agree_area {
  margin-top: 20px;
  label {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: $black;
    .agree_label {
      margin-left: 10px;
    }
  }
}

@media screen and (max-width: $tabletWidth) {
  .user-register {
    padding: 30px 0 20px;

    .description {
      margin-top: 10px;
      padding-left: 20px;
      text-align: left;
    }
  }
}
</style>
